import "isomorphic-fetch";
import {getAccountApiUrl, getAccountsWebUrl} from "../env/env";
import sha256 from "crypto-js/sha256";
import {AuthenticationMethod, getAuthenticationMethod} from "../components/auth/method";

export type OAuthAccessToken = {
  token: string
  provider: string
}

type InitAccountRequest = {
  idToken: string,
  providerUser?: {
    providerName: string
    accessToken: string
  },
  captchaToken?: string
}

export type InitAccountResponse = {
  contentId: string
  accountHash: string
  creationTime: string
  authProvider: string
  isNewSignUp: boolean
}

export type InitAccountOptions = {
  uid: string,
  idToken: string,
  accessToken?: OAuthAccessToken
  captchaToken?: string
}

export type ProviderLoginResponse = {
  customToken: string
  role?: string
  subdomain?: string
};

export type DeleteAccountResponse = {
  deleted: boolean
}

export const MigrationAPI = {
  migrateAccount: async (): Promise<void> => {
    const uri = getAccountsWebUrl("/api/migrate")
    const res =  await fetch(uri, {method: "post"})
    if (res.ok) {
      return;
    }
    return Promise.reject(toResponse(res));
  }
}

export const AccountsAPI = {
  initAccount: async (opts: InitAccountOptions): Promise<InitAccountResponse> => {
    const {uid} = opts;
    const requestBody = toInitAccountRequest(opts);
    const url = getAccountApiUrl(`/accounts/${uid}`);
    const res = await fetch(url, {
      method: "put",
      body: JSON.stringify(requestBody),
      headers: {"Content-Type": "application/json"}
    })
    .then(res => toResponse(res));
    if (res.statusCode !== 200) {
      throw res.body;
    }
    return res.body as InitAccountResponse;
  },

  initiateAuthentication: async (params: OAuthRequestParams): Promise<OAuthResponse> => {
    const requestSearchParams = toSearchParams(params)
    const url = getAccountApiUrl("/auth") + "?" + requestSearchParams;
    const res = await fetch(url, {method: "GET"})
    if (!res.ok) {
      const resp = await toResponse(res);
      return Promise.reject(resp)
    }
    const location = res.headers.get("Location")
    const oauthCookie = res.headers.get("x-wakelet-oauth")
    return {
      location: location || "",
      sessionId: oauthCookie || "",
    }
  },
  getMethod: async (sessionId: string): Promise<AuthenticationMethod> => {
    const url = getAccountApiUrl("/auth/method")
    const headers = {"x-wakelet-oauth": sessionId}
    const res = await fetch(url, {method: "GET", headers})
    const httpResponse = await toResponse(res);
    if (!res.ok) {
      return Promise.reject(httpResponse);
    }
    const method = httpResponse.body.method;
    return getAuthenticationMethod(method);
  },
  setAuthUser: async (idToken: string, sessionId: string): Promise<SetUserIdResponse> => {
    const url = getAccountApiUrl("/auth/user")
    const headers = {
      "x-wakelet-oauth": sessionId,
      Authorization: `Bearer ${idToken}`,
    };
    const res = await fetch(url, {method: "PUT", headers});
    if (!res.ok) {
      const resp = await toResponse(res);
      return Promise.reject(resp);
    }
    return {
      status: res.status,
      location: res.headers.get("Location") || "",
    };
  },
  providerLogin: async (provider: string, code: string): Promise<ProviderLoginResponse> => {
    const url = getAccountApiUrl(`/auth/${provider}/login`);
    const body = JSON.stringify({code});
    const res = await fetch(url, {method: "POST", body})
    const resp = await toResponse(res);
    if (!res.ok) {
      return Promise.reject(resp);
    }
    return resp.body;
  },
  deleteAccount: async (token: string): Promise<DeleteAccountResponse> => {
    const url = getAccountApiUrl("/accounts")
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "authorization": `Bearer ${token}`
        }
      })
      if (res.status !== 200) {
        const {message} = await res.json()
        throw new Error(message)
      }
    } catch (e) {
      console.error("error in delete account", e)
      return {deleted: false}
    }


    return {deleted: true}
  },
  getAvatar: async (email: string): Promise<string> => {
    const url = new URL(getAccountApiUrl("/avatars"))
    url.searchParams.set("email", email);
    const res = await fetch(url.toString(), {method: "GET"})
    const httpResponse = await toResponse(res);
    if (!res.ok) {
      return Promise.reject(httpResponse);
    }
    return httpResponse.body.imageUrl;
  },

  getTempCreds: async(email: string): Promise<string> => {
    const url = new URL(getAccountApiUrl("/temporary-credentials"));
    const res = await fetch(url.toString(), {method: "POST", body: JSON.stringify({email})});
    const httpResponse = await toResponse(res);
    if (!res.ok) {
      return Promise.reject(httpResponse);
    }
    return httpResponse.body.token;
  },
  triggerVerificationEmail: async(email: string): Promise<void> => {
      const uri = getAccountApiUrl("/verification-emails");
      const res = await fetch(uri, {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({email}),
      });
      if (!res.ok) {
          return Promise.reject(toResponse(res));
      }
  },
  verifyEmail: async(oobCode: string): Promise<string> => {
      const uri = getAccountApiUrl("/emails/verified");
      const res = await fetch(uri, {
          method: "PUT",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({oobCode}),
      });
      const httpResponse = await toResponse(res);
      if (!res.ok) {
          return Promise.reject(httpResponse);
      }
      return httpResponse.body.email;
  }
}

const toResponse = async (res: Response): Promise<HttpResponse> => {
  try {
    const body = await res.json();
    return {body: body, statusCode: res.status}
  } catch (err) {
    return {statusCode: res.status, body: undefined}
  }
}

type HttpResponse = {
  statusCode: number;
  body: any;
}

const toInitAccountRequest = (opts: InitAccountOptions): InitAccountRequest => {
  const {idToken, accessToken, captchaToken} = opts;
  return {
    idToken,
    providerUser: accessToken ? {providerName: accessToken.provider, accessToken: accessToken.token} : undefined,
    captchaToken: captchaToken,
  };
};

export type OAuthRequestParams = {
  scope: string
  response_type: string
  code_challenge: string
  code_challenge_method: string;
  client_id: string
  redirect_uri: string
  state?: string
  intent?: string
  prompt?: string
  provider?: string
  provider_code?: string
  provider_scope?: string
}

export type OAuthResponse = {
  location: string
  sessionId: string
}

export type SetUserIdResponse = {
  status: number
  location: string
}

export const hashSHA256 = (value: string): string => {
  return sha256(value).toString()
}

export const toSearchParams = (params: OAuthRequestParams): string => {
  const req: { [key: string]: string } = {};
  Object.entries(params).forEach((entry) => {
    const [key, value] = entry
    if (!value) {
      return
    }
    req[key] = value
  })
  return new URLSearchParams(req).toString();
};
